import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { HelmetProvider } from "react-helmet-async";
import { LanguageProvider, useLanguage } from "./i18n/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Navbar } from "./components/Navbar";
import { Footer } from "./components/Footer";
import { ContactFloat } from "./components/ContactFloat";
import { AuthCallback } from "./components/AuthCallback";
import { AccessibilityWidget } from "./components/AccessibilityWidget";
import { LeafFillAnimation } from "./components/LeafFillAnimation";
import { ZoomResetButton } from "./components/ZoomResetButton";
import { Home } from "./pages/Home";
import { Method } from "./pages/Method";
import { DIY } from "./pages/DIY";
import { Product } from "./pages/Product";
import { Community } from "./pages/Community";
import { WhyOpen } from "./pages/WhyOpen";
import { Articles } from "./pages/Articles";
import { ArticleNaturalDeodorant } from "./pages/ArticleNaturalDeodorant";
import { ArticleSodiumBicarbonate } from "./pages/ArticleSodiumBicarbonate";
import { QuantityCalculator } from "./pages/QuantityCalculator";
import { Terms } from "./pages/Terms";
import { About } from "./pages/About";
import { Admin } from "./pages/Admin";
import { DIYKit } from "./pages/DIYKit";

// Hide external badges
const useHideBadge = () => {
  useEffect(() => {
    const hideBadge = () => {
      // Only hide emergent branding badges, not actual content links
      const badges = document.querySelectorAll('.emergent-badge, #emergent-badge, [data-testid="emergent-badge"]');
      badges.forEach(el => {
        el.style.display = 'none';
      });
    };
    hideBadge();
    const interval = setInterval(hideBadge, 1000);
    return () => clearInterval(interval);
  }, []);
};

// Force RTL/LTR on route change
const useForceDirection = () => {
  const location = useLocation();
  const { language } = useLanguage();
  
  useEffect(() => {
    const dir = language === 'he' ? 'rtl' : 'ltr';
    document.documentElement.setAttribute('dir', dir);
    document.documentElement.setAttribute('lang', language);
    document.body.setAttribute('dir', dir);
    document.body.style.direction = dir;
  }, [location.pathname, language]);
};

// Scroll to top on page change
const useScrollToTop = () => {
  const location = useLocation();
  
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [location.pathname]);
};

function AppRouter() {
  const location = useLocation();
  useHideBadge();
  useForceDirection();
  useScrollToTop();
  
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <Navbar />
      <ZoomResetButton />
      <main className="min-h-screen">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/method" element={<Method />} />
          <Route path="/diy" element={<DIY />} />
          <Route path="/product" element={<Product />} />
          <Route path="/community" element={<Community />} />
          <Route path="/why-open" element={<WhyOpen />} />
          <Route path="/articles" element={<Articles />} />
          <Route path="/articles/natural-deodorant" element={<ArticleNaturalDeodorant />} />
          <Route path="/articles/sodium-bicarbonate" element={<ArticleSodiumBicarbonate />} />
          <Route path="/calculator" element={<QuantityCalculator />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/about" element={<About />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/diy-kit" element={<DIYKit />} />
        </Routes>
      </main>
      <Footer />
      <ContactFloat />
    </>
  );
}

function App() {
  // Check session storage synchronously on first render
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLoading, setShowLoading] = useState(false);
  
  useEffect(() => {
    // Check if user has visited before in this session
    const visited = sessionStorage.getItem('mart_visited');
    if (!visited) {
      setShowLoading(true);
    }
    setIsInitialized(true);
  }, []);
  
  const handleLoadingComplete = () => {
    sessionStorage.setItem('mart_visited', 'true');
    setShowLoading(false);
  };
  
  // Don't render anything until we check session storage
  if (!isInitialized) {
    return null;
  }
  
  // Show initial loading animation only on first visit
  if (showLoading) {
    return (
      <HelmetProvider>
        <ThemeProvider>
        <LanguageProvider>
          <div className="App" dir="rtl">
            <LeafFillAnimation onComplete={handleLoadingComplete} />
          </div>
        </LanguageProvider>
        </ThemeProvider>
      </HelmetProvider>
    );
  }
  
  return (
    <HelmetProvider>
      <ThemeProvider>
      <LanguageProvider>
        <div className="App" dir="rtl">
          <BrowserRouter>
            <AppRouter />
            <AccessibilityWidget />
          </BrowserRouter>
        </div>
      </LanguageProvider>
      </ThemeProvider>
    </HelmetProvider>
  );
}

export default App;
